import api from './api.service';
import type { RejectionPayload } from './rejection.service';

function unwrap<T>(res: unknown): T {
  const data = (res as any)?.data ?? res;
  return (data?.data ?? data) as T;
}

export interface ApprovalWorkflowDefinitionDTO {
  id: string;
  workflow_code: string;
  workflow_name: string;
  module?: string;
  entity_type: string;
  description?: string;
  version: number;
  is_active: boolean;
  trigger_mode: string;
  amount_min?: number;
  amount_max?: number;
  condition_json?: Record<string, unknown>;
  steps?: ApprovalWorkflowStepDTO[];
}

export interface ApprovalWorkflowStepDTO {
  id?: string;
  step_number: number;
  level_name?: string;
  /** Signature block heading on printouts; blank → Approved by. */
  print_role_label?: string | null;
  approver_type: string;
  approver_role_id?: string;
  approver_user_id?: string;
  dynamic_approver_rule?: string;
  /** document | fixed — for department_process_owner */
  department_source?: string;
  department_id?: string;
  is_optional?: boolean;
  is_parallel?: boolean;
  escalation_hours?: number;
  escalation_role_id?: string;
  escalation_user_id?: string;
}

export interface ApprovalInstanceDTO {
  id: string;
  status: string;
  current_step_number: number;
  entity_type: string;
  entity_id: string;
  document_number?: string;
  submitted_at?: string;
  completed_at?: string;
  /** Present when submit stored context (e.g. budget exceedance reason / amount_over). */
  metadata_json?: Record<string, unknown> | null;
  instanceSteps?: ApprovalInstanceStepDTO[];
  pendingApprovers?: PendingApproverDTO[];
  pendingApproverLabel?: string;
  /** Set on inbox rows the current user sees only through an approval delegation. */
  delegated_from_user_id?: string;
}

export interface PendingApproverRoleMember {
  id: string;
  full_name: string;
  email?: string;
}

export interface PendingApproverDTO {
  step_number: number;
  assignee_type: 'user' | 'role';
  user_id?: string;
  full_name?: string;
  email?: string;
  role_id?: string;
  role_name?: string;
  role_members?: PendingApproverRoleMember[];
  /** Active approval delegates covering this user. */
  delegates?: PendingApproverRoleMember[];
}

export interface ApprovalSummaryDTO {
  instance: ApprovalInstanceDTO | null;
  history: ApprovalActionLogDTO[];
  pendingApprovers?: PendingApproverDTO[];
  pendingApproverLabel?: string;
  canCurrentUserAct?: boolean;
  can_revoke?: boolean;
  revoke_as_override?: boolean;
  revoke_block_reasons?: string[];
}

export interface PendingApproversBatchDTO {
  approvers: Record<string, PendingApproverDTO[]>;
  labels: Record<string, string>;
}

export interface ApprovalInstanceStepDTO {
  id: string;
  step_number: number;
  status: string;
  level_name?: string;
  assigned_user_id?: string;
  assigned_role_id?: string;
  acted_by?: string;
  acted_at?: string;
  action_taken?: string;
  remarks?: string;
}

export interface ApprovalActionLogDTO {
  id: string;
  action_taken: string;
  actor_user_id?: string;
  actor_full_name?: string;
  actor_email?: string;
  remarks?: string;
  step_number?: number;
  created_at: string;
}

/** Approvers for document print (name, designation, signature image). */
export interface ApprovalPrintSigner {
  step_number: number;
  acted_at?: string;
  remarks?: string;
  user: {
    id: string;
    full_name: string;
    designation?: string | null;
    signature_image_url?: string | null;
  };
}

export const approvalService = {
  async listDefinitions(params?: { entity_type?: string; is_active?: boolean }): Promise<ApprovalWorkflowDefinitionDTO[]> {
    const q = new URLSearchParams();
    if (params?.entity_type) q.set('entity_type', params.entity_type);
    if (params?.is_active !== undefined) q.set('is_active', String(params.is_active));
    const res = await api.get(`/approval/definitions?${q.toString()}`);
    const raw = unwrap<ApprovalWorkflowDefinitionDTO[]>(res);
    return Array.isArray(raw) ? raw : [];
  },

  async getDefinition(id: string): Promise<ApprovalWorkflowDefinitionDTO> {
    const res = await api.get(`/approval/definitions/${id}`);
    return unwrap<ApprovalWorkflowDefinitionDTO>(res);
  },

  async createDefinition(body: {
    workflow_code: string;
    workflow_name: string;
    module?: string;
    entity_type: string;
    description?: string;
    trigger_mode?: string;
    amount_min?: number | null;
    amount_max?: number | null;
    condition_json?: Record<string, unknown>;
    is_active?: boolean;
    steps: ApprovalWorkflowStepDTO[];
  }): Promise<ApprovalWorkflowDefinitionDTO> {
    const res = await api.post('/approval/definitions', body);
    return unwrap<ApprovalWorkflowDefinitionDTO>(res);
  },

  async updateDefinition(
    id: string,
    body: Partial<{
      workflow_name: string;
      module: string;
      description: string;
      trigger_mode: string;
      amount_min: number | null;
      amount_max: number | null;
      condition_json: Record<string, unknown>;
      is_active: boolean;
      steps: ApprovalWorkflowStepDTO[];
    }>
  ): Promise<ApprovalWorkflowDefinitionDTO> {
    const res = await api.put(`/approval/definitions/${id}`, body);
    return unwrap<ApprovalWorkflowDefinitionDTO>(res);
  },

  async deleteDefinition(id: string): Promise<void> {
    await api.delete(`/approval/definitions/${id}`);
  },

  async cloneDefinition(id: string, workflow_code: string): Promise<ApprovalWorkflowDefinitionDTO> {
    const res = await api.post(`/approval/definitions/${id}/clone`, { workflow_code });
    return unwrap<ApprovalWorkflowDefinitionDTO>(res);
  },

  async getSummary(entityType: string, entityId: string): Promise<ApprovalSummaryDTO> {
    const res = await api.get(`/approval/summary?entity_type=${encodeURIComponent(entityType)}&entity_id=${encodeURIComponent(entityId)}`);
    return unwrap(res);
  },

  async getPendingApprovers(instanceIds: string[]): Promise<PendingApproversBatchDTO> {
    const unique = [...new Set(instanceIds.filter(Boolean))];
    if (!unique.length) {
      return { approvers: {}, labels: {} };
    }
    const res = await api.get(
      `/approval/pending-approvers?instance_ids=${encodeURIComponent(unique.join(','))}`
    );
    return unwrap(res);
  },

  async getPrintSigners(entityType: string, entityId: string): Promise<ApprovalPrintSigner[]> {
    const res = await api.get(
      `/approval/print-signers?entity_type=${encodeURIComponent(entityType)}&entity_id=${encodeURIComponent(entityId)}`
    );
    const raw = unwrap<{ signers?: ApprovalPrintSigner[] }>(res);
    return Array.isArray(raw?.signers) ? raw.signers : [];
  },

  async approveInstance(instanceId: string, remarks?: string): Promise<ApprovalInstanceDTO> {
    const res = await api.post(`/approval/instances/${instanceId}/approve`, { remarks });
    return unwrap<ApprovalInstanceDTO>(res);
  },

  async rejectInstance(instanceId: string, payload: RejectionPayload): Promise<ApprovalInstanceDTO> {
    const res = await api.post(`/approval/instances/${instanceId}/reject`, payload);
    return unwrap<ApprovalInstanceDTO>(res);
  },

  async revokeInstance(instanceId: string, reason: string): Promise<ApprovalInstanceDTO> {
    const res = await api.post(`/approval/instances/${instanceId}/revoke`, { reason });
    return unwrap<ApprovalInstanceDTO>(res);
  },

  /** Submitter manual remind (once per day); reuses Approval notification dispatch. */
  async remindInstance(instanceId: string): Promise<{ sent: number; deduped: boolean }> {
    const res = await api.post(`/approval/instances/${instanceId}/remind`, {});
    return unwrap(res);
  },

  async inbox(
    filter?: 'pending' | 'overdue' | 'escalated' | 'returned',
    sort: 'impact' | 'due' | 'created' = 'impact'
  ): Promise<ApprovalInstanceDTO[]> {
    const q = new URLSearchParams();
    if (filter) q.set('filter', filter);
    if (sort) q.set('sort', sort);
    const qs = q.toString() ? `?${q.toString()}` : '';
    const res = await api.get(`/approval/inbox${qs}`);
    const raw = unwrap<ApprovalInstanceDTO[]>(res);
    return Array.isArray(raw) ? raw : [];
  },

  async notificationSummary(): Promise<{
    greeting: string;
    unread_total: number;
    urgent: number;
    normal: number;
    escalated: number;
    estimated_review_minutes: number;
    highest_priority_title: string | null;
  }> {
    const res = await api.get('/notifications/summary');
    return unwrap(res);
  },

  async notificationTimeline(approvalInstanceId: string): Promise<
    Array<{
      id: string;
      approval_event_id: string;
      channel: string;
      status: string;
      failed_reason?: string;
      created_at: string;
    }>
  > {
    const res = await api.get(
      `/notifications/timeline?approval_instance_id=${encodeURIComponent(approvalInstanceId)}`
    );
    const raw = unwrap(res);
    return Array.isArray(raw) ? raw : [];
  },

  async checkNotificationAccess(params: {
    entityType?: string;
    entityId?: string;
    instanceId?: string;
  }): Promise<{ allowed: boolean; reason?: string; actionUrl?: string; fallbackUrl: string }> {
    const q = new URLSearchParams();
    if (params.entityType) q.set('entity_type', params.entityType);
    if (params.entityId) q.set('entity_id', params.entityId);
    if (params.instanceId) q.set('instance_id', params.instanceId);
    const res = await api.get(`/approval/inbox/check-access?${q.toString()}`);
    return unwrap(res);
  },
};
